import { EventStatusTone, eventStatusTone } from '../../../../../assets/constData/event_status';

export type MapPinTone = EventStatusTone;

export interface GeoPoint {
  readonly lat: number;
  readonly lng: number;
}

export interface EventVenueLocation {
  readonly address: string;
  readonly mapsUrl: string;
  readonly point: GeoPoint | null;
}

const COORD_HINTS = new Set([
  'latitude',
  'longitude',
  'lat',
  'lng',
  'lon',
  'geo_lat',
  'geo_lng',
  'geo_lon',
]);

const PLACE_HINTS = new Set([
  'venue',
  'address',
  'location',
  'city',
  'town',
  'state',
  'province',
  'region',
  'country',
  'zip',
  'zipcode',
  'postal_code',
  'postcode',
  'pincode',
]);

const AT_COORD = /@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/;
const QUERY_COORD = /[?&](?:q|query|ll)=(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/i;

export function parseFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function isValidLatLng(lat: number, lng: number): boolean {
  return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

export function parseLatLngFromMapsUrl(url: string): GeoPoint | null {
  if (!url) {
    return null;
  }
  const atMatch = AT_COORD.exec(url);
  if (atMatch) {
    return toPoint(atMatch[1], atMatch[2]);
  }
  const queryMatch = QUERY_COORD.exec(url);
  if (queryMatch) {
    return toPoint(queryMatch[1], queryMatch[2]);
  }
  return null;
}

function toPoint(latRaw: string, lngRaw: string): GeoPoint | null {
  const lat = Number(latRaw);
  const lng = Number(lngRaw);
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || !isValidLatLng(lat, lng)) {
    return null;
  }
  return { lat, lng };
}

type VenueField = {
  field_hint?: unknown;
  field_title?: unknown;
  field_type?: unknown;
  field_value?: unknown;
};

function hintKey(field: VenueField): string {
  const raw = [field.field_hint, field.field_title, field.field_type]
    .map((part) => String(part ?? '').trim())
    .find((part) => part.length > 0);
  return (raw ?? '').toLowerCase().replaceAll(' ', '_');
}

function isLatHint(key: string): boolean {
  return key === 'lat' || key === 'latitude' || key === 'geo_lat';
}

function isGooglePlaceField(key: string, text: string): boolean {
  return key.includes('google') || key.includes('maps') || looksLikeMapsUrl(text);
}

export function parseGooglePlaceValue(value: unknown): {
  readonly point: GeoPoint | null;
  readonly address: string;
} {
  const record = coerceJsonRecord(value);
  if (!record) {
    return { point: null, address: '' };
  }
  const lat = parseFiniteNumber(record['lat'] ?? record['latitude']);
  const lng = parseFiniteNumber(record['lng'] ?? record['lon'] ?? record['longitude']);
  const point =
    lat != null && lng != null && isValidLatLng(lat, lng) ? { lat, lng } : null;
  const formatted = scalarText(record['formatted_address'] ?? record['address']);
  return {
    point,
    address: formatted || scalarText(record['name']),
  };
}

type VenueAcc = {
  parts: string[];
  googleAddress: string;
  lat: number | null;
  lng: number | null;
  mapsUrl: string;
};

export function extractEventVenueLocation(event: unknown): EventVenueLocation {
  const record = asRecord(event);
  const fromVenue = locationFromFirstEventVenue(record?.['event_venues']);
  if (fromVenue) {
    return fromVenue;
  }
  const acc: VenueAcc = {
    parts: [],
    googleAddress: '',
    lat: null,
    lng: null,
    mapsUrl: '',
  };
  for (const field of collectVenueFields(record?.['extra_json'])) {
    applyVenueField(field, acc);
  }

  const topLevelVenue = readFieldText(record?.['venue_name'] ?? record?.['venue']);
  if (topLevelVenue && !acc.googleAddress) {
    acc.parts.unshift(topLevelVenue);
  }

  const fromUrl = parseLatLngFromMapsUrl(acc.mapsUrl);
  const point =
    acc.lat != null && acc.lng != null && isValidLatLng(acc.lat, acc.lng)
      ? { lat: acc.lat, lng: acc.lng }
      : fromUrl;

  return {
    address: acc.googleAddress || uniqueJoin(acc.parts),
    mapsUrl: acc.mapsUrl,
    point,
  };
}

function locationFromFirstEventVenue(eventVenues: unknown): EventVenueLocation | null {
  const links = asArray(eventVenues);
  if (links.length === 0) {
    return null;
  }
  const locations = links
    .map((item) => venueLinkLocation(item))
    .filter((item): item is EventVenueLocation => item != null);
  if (locations.length === 0) {
    return { address: '', mapsUrl: '', point: null };
  }
  return locations.find((item) => item.point) ?? locations[0];
}

function venueLinkLocation(item: unknown): EventVenueLocation | null {
  const link = asRecord(item);
  if (!link) {
    return null;
  }
  const venue = asRecord(link['venue']) ?? link;
  const lat = parseFiniteNumber(venue['latitude'] ?? venue['lat']);
  const lng = parseFiniteNumber(venue['longitude'] ?? venue['lng'] ?? venue['lon']);
  const point =
    lat != null && lng != null && isValidLatLng(lat, lng) ? { lat, lng } : null;
  const addressText = scalarText(venue['address']);
  return {
    address: addressText.length > 0 ? addressText : scalarText(venue['name']),
    mapsUrl: '',
    point,
  };
}

function applyVenueField(field: VenueField, acc: VenueAcc): void {
  const key = hintKey(field);
  const text = readFieldText(field.field_value);
  if (COORD_HINTS.has(key) && isLatHint(key)) {
    acc.lat = parseFiniteNumber(field.field_value) ?? parseFiniteNumber(text) ?? acc.lat;
    return;
  }
  if (COORD_HINTS.has(key)) {
    acc.lng = parseFiniteNumber(field.field_value) ?? parseFiniteNumber(text) ?? acc.lng;
    return;
  }
  if (isGooglePlaceField(key, text)) {
    applyGooglePlaceField(field.field_value, text, acc);
    return;
  }
  if (PLACE_HINTS.has(key) && text) {
    acc.parts.push(text);
  }
}

function applyGooglePlaceField(raw: unknown, text: string, acc: VenueAcc): void {
  const place = parseGooglePlaceValue(raw);
  if (place.point) {
    acc.lat = acc.lat ?? place.point.lat;
    acc.lng = acc.lng ?? place.point.lng;
  }
  if (place.address) {
    acc.googleAddress = acc.googleAddress || place.address;
  }
  const fromText = parseLatLngFromMapsUrl(text);
  if (fromText) {
    acc.lat = acc.lat ?? fromText.lat;
    acc.lng = acc.lng ?? fromText.lng;
  }
  if (looksLikeMapsUrl(text)) {
    acc.mapsUrl = text;
  }
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function scalarText(value: unknown): string {
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value).trim();
  }
  return '';
}

function coerceJsonRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value === 'string' && value.trim().startsWith('{')) {
    try {
      return asRecord(JSON.parse(value));
    } catch {
      return null;
    }
  }
  return asRecord(value);
}

function parseExtraJson(extraJson: unknown): unknown[] {
  if (Array.isArray(extraJson)) {
    return extraJson;
  }
  if (typeof extraJson !== 'string' || !extraJson.trim()) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(extraJson);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function sectionFields(section: unknown): unknown[] {
  const record = asRecord(section);
  return [
    ...asArray(record?.['section_form_admin_values']),
    ...asArray(record?.['section_json_with_values']),
    ...asArray(record?.['section_json']),
  ];
}

function isVenueSection(fields: unknown[]): boolean {
  return fields.some((field) => {
    const record = asRecord(field);
    if (!record) {
      return false;
    }
    const key = hintKey({
      field_hint: record['field_hint'],
      field_title: record['field_title'],
      field_type: record['field_type'],
    });
    return (
      key === 'venue' ||
      key === 'address' ||
      key === 'location' ||
      key.includes('google')
    );
  });
}

function collectVenueFields(extraJson: unknown): VenueField[] {
  const fields: VenueField[] = [];
  for (const section of parseExtraJson(extraJson)) {
    const values = sectionFields(section);
    if (!isVenueSection(values)) {
      continue;
    }
    for (const field of values) {
      const record = asRecord(field);
      if (!record) {
        continue;
      }
      fields.push({
        field_hint: record['field_hint'],
        field_title: record['field_title'],
        field_type: record['field_type'],
        field_value: record['field_value'],
      });
    }
  }
  return fields;
}

function readFieldText(value: unknown): string {
  if (value == null) {
    return '';
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value).trim();
  }
  if (Array.isArray(value)) {
    return uniqueJoin(value.map((item) => readFieldText(item)));
  }
  const record = asRecord(value);
  if (!record) {
    return '';
  }
  return readFieldText(
    record['field_value'] ??
      record['value'] ??
      record['label'] ??
      record['name'] ??
      record['address'],
  );
}

function looksLikeMapsUrl(value: string): boolean {
  const lower = value.toLowerCase();
  return (
    lower.includes('google.com/maps') ||
    lower.includes('maps.google') ||
    lower.includes('maps.app.goo.gl')
  );
}

function uniqueJoin(parts: string[]): string {
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed) {
      continue;
    }
    const key = trimmed.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    unique.push(trimmed);
  }
  return unique.join(', ');
}

export function lngLatToWorld(lng: number, lat: number, zoom: number): GeoPoint {
  const n = 2 ** zoom;
  const x = ((lng + 180) / 360) * n;
  const clampedLat = Math.min(85.05112878, Math.max(-85.05112878, lat));
  const latRad = (clampedLat * Math.PI) / 180;
  const y =
    ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n;
  return { lat: y, lng: x };
}

export function worldToLngLat(worldX: number, worldY: number, zoom: number): GeoPoint {
  const n = 2 ** zoom;
  const lng = (worldX / n) * 360 - 180;
  const latRad = Math.atan(Math.sinh(Math.PI * (1 - (2 * worldY) / n)));
  const lat = (latRad * 180) / Math.PI;
  return { lat, lng };
}

export interface MapBounds {
  readonly north: number;
  readonly south: number;
  readonly east: number;
  readonly west: number;
}

export interface MapViewport {
  readonly width: number;
  readonly height: number;
  readonly zoom: number;
  readonly center: GeoPoint;
}

const LAT_LIMIT = 90;
const LNG_LIMIT = 180;

function clampToLimit(value: number, limit: number): number {
  return Math.min(limit, Math.max(-limit, value));
}

/** Rounded to ~100m so a tiny pan does not refetch the events API. */
export function queryBounds(bounds: MapBounds): MapBounds {
  const round = (value: number) => Math.round(value * 1000) / 1000;
  return {
    north: round(bounds.north),
    south: round(bounds.south),
    east: round(bounds.east),
    west: round(bounds.west),
  };
}

export function viewportBounds(view: MapViewport): MapBounds {
  const world = lngLatToWorld(view.center.lng, view.center.lat, view.zoom);
  const halfTilesX = view.width / 512;
  const halfTilesY = view.height / 512;
  const nw = worldToLngLat(world.lng - halfTilesX, world.lat - halfTilesY, view.zoom);
  const se = worldToLngLat(world.lng + halfTilesX, world.lat + halfTilesY, view.zoom);
  return {
    north: clampToLimit(Math.max(nw.lat, se.lat), LAT_LIMIT),
    south: clampToLimit(Math.min(nw.lat, se.lat), LAT_LIMIT),
    west: clampToLimit(Math.min(nw.lng, se.lng), LNG_LIMIT),
    east: clampToLimit(Math.max(nw.lng, se.lng), LNG_LIMIT),
  };
}

export function pointInBounds(
  point: GeoPoint,
  bounds: { north: number; south: number; east: number; west: number },
): boolean {
  const latOk = point.lat <= bounds.north && point.lat >= bounds.south;
  if (!latOk) {
    return false;
  }
  if (bounds.west <= bounds.east) {
    return point.lng >= bounds.west && point.lng <= bounds.east;
  }
  return point.lng >= bounds.west || point.lng <= bounds.east;
}

export interface ClusterablePin {
  readonly id: string;
  readonly point: GeoPoint;
}

export interface PinCluster {
  readonly id: string;
  readonly point: GeoPoint;
  readonly memberIds: readonly string[];
}

export function clusterPins(
  pins: readonly ClusterablePin[],
  zoom: number,
  pixelRadius = 48,
): PinCluster[] {
  const clusters: PinCluster[] = [];
  const claimed = new Set<string>();

  for (const pin of pins) {
    if (claimed.has(pin.id)) {
      continue;
    }
    const origin = lngLatToWorld(pin.point.lng, pin.point.lat, zoom);
    const members = [pin.id];
    claimed.add(pin.id);

    for (const other of pins) {
      if (claimed.has(other.id)) {
        continue;
      }
      const world = lngLatToWorld(other.point.lng, other.point.lat, zoom);
      const dx = (world.lng - origin.lng) * 256;
      const dy = (world.lat - origin.lat) * 256;
      if (dx * dx + dy * dy <= pixelRadius * pixelRadius) {
        members.push(other.id);
        claimed.add(other.id);
      }
    }

    clusters.push({
      id: members.length === 1 ? pin.id : `cluster-${pin.id}`,
      point: pin.point,
      memberIds: members,
    });
  }

  return clusters;
}

export function mapPinTone(statusId: unknown): MapPinTone {
  return eventStatusTone(statusId);
}

export function fitCenter(points: readonly GeoPoint[]): GeoPoint {
  if (points.length === 0) {
    return { lat: 20, lng: 0 };
  }
  const sum = points.reduce(
    (acc, point) => ({ lat: acc.lat + point.lat, lng: acc.lng + point.lng }),
    { lat: 0, lng: 0 },
  );
  return { lat: sum.lat / points.length, lng: sum.lng / points.length };
}

export function zoomToFit(
  points: readonly GeoPoint[],
  width: number,
  height: number,
): { readonly center: GeoPoint; readonly zoom: number } {
  const center = fitCenter(points);
  if (points.length <= 1) {
    return { center, zoom: 12 };
  }
  const viewW = Math.max(1, width - Math.max(80, width * 0.2));
  const viewH = Math.max(1, height - Math.max(80, height * 0.2));
  for (let zoom = 14; zoom >= 5; zoom -= 1) {
    const bounds = viewportBounds({ width: viewW, height: viewH, zoom, center });
    if (points.every((point) => pointInBounds(point, bounds))) {
      return { center, zoom };
    }
  }
  return { center, zoom: 5 };
}

export interface OsmTile {
  readonly key: string;
  readonly url: string;
  readonly left: number;
  readonly top: number;
}

const TILE_SIZE = 256;

export const MAP_MIN_ZOOM = 2;
export const MAP_MAX_ZOOM = 22;
const OSM_TILE_MAX_ZOOM = 19;
const METERS_PER_PIXEL_AT_ZOOM_0 = 156543.03392;
const SCALE_BAR_MAX_PX = 80;
const SCALE_STEPS_M = [
  1, 2, 5, 10, 20, 50, 100, 200, 500,
  1000, 2000, 5000, 10000, 20000, 50000,
  100000, 200000, 500000, 1000000, 2000000, 5000000,
];

export function clampMapZoom(zoom: number): number {
  return Math.min(MAP_MAX_ZOOM, Math.max(MAP_MIN_ZOOM, zoom));
}

export function osmTileZoom(zoom: number): number {
  return Math.min(OSM_TILE_MAX_ZOOM, Math.max(0, Math.floor(zoom)));
}

export function osmTileScale(zoom: number): number {
  return 2 ** (zoom - osmTileZoom(zoom));
}

export interface MapScale {
  readonly label: string;
  readonly widthPx: number;
}

/** Ground distance for a bar about 80px wide, using the same steps as a maps scale (2 m, 100 m, 1 km). */
export function mapScale(lat: number, zoom: number): MapScale {
  const cosLat = Math.cos((lat * Math.PI) / 180);
  const metersPerPixel = (METERS_PER_PIXEL_AT_ZOOM_0 * Math.abs(cosLat)) / 2 ** zoom;
  const maxMeters = metersPerPixel * SCALE_BAR_MAX_PX;
  let meters = SCALE_STEPS_M[0];
  for (const step of SCALE_STEPS_M) {
    if (step <= maxMeters) {
      meters = step;
    }
  }
  return {
    label: meters >= 1000 ? `${meters / 1000} km` : `${meters} m`,
    widthPx: Math.round(meters / metersPerPixel),
  };
}

export function osmTiles(view: MapViewport): OsmTile[] {
  const z = osmTileZoom(view.zoom);
  const world = lngLatToWorld(view.center.lng, view.center.lat, z);
  const n = 2 ** z;
  const originPxX = world.lng * TILE_SIZE - view.width / 2;
  const originPxY = world.lat * TILE_SIZE - view.height / 2;
  const startX = Math.floor(originPxX / TILE_SIZE);
  const startY = Math.floor(originPxY / TILE_SIZE);
  const endX = Math.floor((originPxX + view.width) / TILE_SIZE);
  const endY = Math.floor((originPxY + view.height) / TILE_SIZE);
  const tiles: OsmTile[] = [];

  for (let x = startX; x <= endX; x += 1) {
    for (let y = startY; y <= endY; y += 1) {
      if (y < 0 || y >= n) {
        continue;
      }
      const wrappedX = ((x % n) + n) % n;
      tiles.push({
        key: `${z}-${wrappedX}-${y}`,
        url: `https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/${z}/${y}/${wrappedX}`,
        left: x * TILE_SIZE - originPxX,
        top: y * TILE_SIZE - originPxY,
      });
    }
  }
  return tiles;
}

export function pinScreenPosition(
  point: GeoPoint,
  view: MapViewport,
): { left: number; top: number } {
  const world = lngLatToWorld(point.lng, point.lat, view.zoom);
  const center = lngLatToWorld(view.center.lng, view.center.lat, view.zoom);
  return {
    left: (world.lng - center.lng) * TILE_SIZE + view.width / 2,
    top: (world.lat - center.lat) * TILE_SIZE + view.height / 2,
  };
}

/** Geographic point currently drawn at a pane-local pixel. */
export function pointAtScreen(view: MapViewport, x: number, y: number): GeoPoint {
  const center = lngLatToWorld(view.center.lng, view.center.lat, view.zoom);
  const worldX = center.lng + (x - view.width / 2) / TILE_SIZE;
  const worldY = center.lat + (y - view.height / 2) / TILE_SIZE;
  return worldToLngLat(worldX, worldY, view.zoom);
}

/** Map center that keeps `anchor` drawn at the given pane-local pixel. */
export function centerForPoint(
  anchor: GeoPoint,
  view: Pick<MapViewport, 'width' | 'height'>,
  focusX: number,
  focusY: number,
  zoom: number,
): GeoPoint {
  const anchorWorld = lngLatToWorld(anchor.lng, anchor.lat, zoom);
  const centerX = anchorWorld.lng - (focusX - view.width / 2) / TILE_SIZE;
  const centerY = anchorWorld.lat - (focusY - view.height / 2) / TILE_SIZE;
  return worldToLngLat(centerX, centerY, zoom);
}

/** New map center so the place under (focusX, focusY) stays there after a zoom change. */
export function zoomToward(
  view: MapViewport,
  focusX: number,
  focusY: number,
  nextZoom: number,
): GeoPoint {
  return centerForPoint(pointAtScreen(view, focusX, focusY), view, focusX, focusY, nextZoom);
}

const EARTH_RADIUS_KM = 6371;

export function distanceKm(from: GeoPoint, to: GeoPoint): number {
  const dLat = toRad(to.lat - from.lat);
  const dLng = toRad(to.lng - from.lng);
  const lat1 = toRad(from.lat);
  const lat2 = toRad(to.lat);
  const haversine =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(haversine)));
}

function toRad(degrees: number): number {
  return (degrees * Math.PI) / 180;
}
